import JsPDF from 'jspdf';
import JsBarcode from 'jsbarcode';
import moment from 'moment';
import { findIndex } from 'lodash';
import generateBarcode from 'pdf417';

const generateCode128Image = (payload) => {
  const canvas = document.createElement('canvas');
  JsBarcode(canvas, payload, {
    format: 'CODE128',
    width: 2,
    height: 60,
    displayValue: false,
    margin: 4,
  });
  return { dataUrl: canvas.toDataURL('image/png'), aspectRatio: canvas.height / canvas.width };
};

// Pack Later label (existing Amazon 2D barcode flow — kept for pack_later)
export const mix2DBarcode = ({ shipmentId = '', items = [] }) => {
  const mappedItem = items.map(({ fnsku, quantity, expiryDate }) => ({
    fnsku,
    qty: quantity,
    exp: expiryDate?.[0] ? moment(expiryDate[0]).format('YYMMDD') : null,
  }));
  let stringToEncode = `AMZN,PO:${shipmentId}`;
  mappedItem.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (item[key]) {
        stringToEncode += `,${key.toUpperCase()}:${item[key].toString().toUpperCase()}`;
      }
    });
  });
  return generateBarcode(stringToEncode, 15, 15);
};

// Pack Now label — new design matching warehouse barcode scanner format
const generatePackNowLabel = ({ boxCode, boxNumber, items = [], dimensions = {}, weight, createdAt }) => {
  const PAGE_W = 288; // 4in at 72pt
  const PAGE_H = 432; // 6in at 72pt
  const doc = new JsPDF('p', 'pt', [PAGE_W, PAGE_H]);
  const margin = 12;
  const contentW = PAGE_W - margin * 2;
  let y = margin;

  doc.setFont('helvetica');

  const { dataUrl: barcodeImg, aspectRatio } = generateCode128Image(boxCode);
  const barcodeW = contentW * 0.75;
  const barcodeH = Math.round(barcodeW * aspectRatio);
  const barcodeX = margin + (contentW - barcodeW) / 2;
  doc.addImage(barcodeImg, 'PNG', barcodeX, y, barcodeW, barcodeH, undefined, 'FAST');
  y += barcodeH + 8;

  // Code text
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`${boxCode}`, PAGE_W / 2, y, { align: 'center' });
  y += 18;

  // BOX CONTENT INFORMATION header
  doc.setFillColor(0, 0, 0);
  doc.rect(margin, y, contentW, 16, 'F');
  doc.setFontSize(7.5);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text('BOX CONTENT INFORMATION', PAGE_W / 2, y + 11, { align: 'center' });
  doc.setTextColor(0, 0, 0);
  y += 30;

  // Subheading
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.text('PLEASE COVER LABEL PRIOR TO SHIPPING TO AMAZON', PAGE_W / 2, y - 4, { align: 'center' });
  y += 18;

  // Box info row
  const totalUnits = items.reduce((sum, i) => sum + Number(i.quantity || i.units || 0), 0);
  const boxType = items.length > 1 ? 'Multi SKU Box' : 'Single SKU Box';
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(boxType, margin, y);
  doc.text(`Total Units: ${totalUnits}`, PAGE_W - margin, y, { align: 'right' });
  y += 25;

  // Item rows
  doc.setFontSize(7.5);
  items.forEach((item) => {
    const name = item.name || item.itemName || item.item_name || '';
    const msku = item.msku || item.sku || '';
    const fnsku = item.fnsku || item.fulfillmentNetworkSku || 'N/A';
    const qty = Number(item.quantity || item.units || 0);

    if (name) {
      const trimmedName = name.length > 50 ? name.substring(0, 48) + '...' : name;
      doc.setFont('helvetica', 'normal');
      doc.text(trimmedName, margin, y);
      y += 11;
    }

    const skuLine = [msku && `SKU: ${msku}`, `FNSKU: ${fnsku}`].filter(Boolean).join('   ');
    doc.setFont('helvetica', 'normal');
    doc.text(skuLine, margin, y);
    doc.setFont('helvetica', 'normal');
    doc.text(`QTY: ${qty}`, PAGE_W - margin, y, { align: 'right' });
    y += 18;
  });

  // Footer
  const footerY = PAGE_H - margin - 18;

  const dateStr = createdAt ? moment(createdAt).format('MMM DD YYYY h:mm A') : moment().format('MMM DD YYYY h:mm A');
  const createdStr = `Created: ${moment(createdAt || new Date()).format('MM-DD-YYYY')}`;
  const dimStr = dimensions.length && dimensions.width && dimensions.height
    ? `${dimensions.length} x ${dimensions.width} x ${dimensions.height} in`
    : '';
  const weightStr = weight ? `${weight} lbs` : '';

  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text(`${dateStr}`, margin, footerY + 8);
  doc.text(createdStr, PAGE_W - margin, footerY + 8, { align: 'right' });
  if (dimStr) doc.text(dimStr, margin, footerY + 18);
  if (weightStr) doc.text(weightStr, PAGE_W - margin, footerY + 18, { align: 'right' });

  window.open(doc.output('bloburl'), '_blank');
};

const box2DLabels = (box) => {
  const {
    boxCode,
    boxNumber,
    shipmentId,
    shipmentName = '',
    items = [],
    dimensions,
    weight,
    createdAt,
    isPackNow,
  } = box;

  if (isPackNow && boxCode) {
    generatePackNowLabel({ boxCode, boxNumber, items, dimensions, weight, createdAt });
    return true;
  }

  // Pack Later: legacy flow
  const barcode = mix2DBarcode({ shipmentId, items });
  let totalQuantity = 0;
  const list = [];
  items.forEach(({ units, fulfillmentNetworkSku }) => {
    const fnsku = fulfillmentNetworkSku || 'N/A';
    totalQuantity += Number(units || 0);
    const index = findIndex(list, value => value.includes(fnsku));
    if (index !== -1) {
      const listItems = list[index].split(': ');
      const itemQty = Number(listItems.pop());
      list[index] = `${fnsku}: ${Number(units || 0) + itemQty}`;
    } else {
      list.push(`${fnsku}: ${Number(units || 0)}`);
    }
  });

  const width = 432;
  const height = 288;
  let doc = new JsPDF('p', 'pt', [width, height]);
  doc.setLineWidth(1);
  doc.rect(3, 4, height - 6, width - 6);

  let yPos = 25;
  const x = 18;
  const centerX = width / 2;
  doc.setFont('sans-serif');
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.text('Box Contents', centerX, yPos, 'center');
  yPos += 25;
  doc.setFontSize(13);
  doc.text('Units: ', x, yPos);
  yPos += 19;
  doc.setFont(undefined, 'normal');
  doc.text(`${totalQuantity}`, x, yPos);
  doc.line(3, 84, 285, 84);
  yPos += 25;
  doc.addImage(barcode, 'PNG', 22, yPos, 245, 90, undefined, 'FAST');
  yPos += 110;
  doc.setFont(undefined, 'bold');
  doc.text('Shipment Name: ', x, yPos);
  doc.text('ID: ', centerX, yPos);
  yPos += 17;
  doc.setFont(undefined, 'normal');
  doc.text(`${shipmentName.length > 15 ? `${shipmentName.substring(0, 13)}...` : shipmentName}`, x, yPos);
  doc.text(`${shipmentId}`, centerX, yPos);
  doc.line(3, yPos + 10, 285, yPos + 10);
  yPos += 20;
  doc.setFontSize(9);
  list.forEach((text) => {
    doc.text(text, x + 1, yPos);
    yPos += 15;
  });
  doc.line(3, height - 35, 285, height - 35);
  doc.setFontSize(13);
  doc.text('Thank you for shipping', x - 4, height - 15);

  window.open(doc.output('bloburl'), '_blank');
  return true;
};

export default box2DLabels;
